import React, { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LayoutDashboard, FolderKanban, LogOut, Settings as SettingsIcon, Archive, KeyRound, HelpCircle, ChevronDown, Inbox, Receipt, Bell } from "lucide-react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/", label: "Engagement Overview", icon: FolderKanban, testId: "nav-overview" },
    { to: "/service-charge-info", label: "Client Uploads", icon: Inbox, testId: "nav-scir" },
    { to: "/quotes", label: "Quotes", icon: Receipt, testId: "nav-quotes" },
    { to: "/archived", label: "Finalised & Archived", icon: Archive, testId: "nav-archived" },
    { to: "/help", label: "Help", icon: HelpCircle, testId: "nav-help" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

const ADMIN_PATHS = ["/service-charge-info", "/quotes"];

function NotificationBell() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ items: [], unread: 0 });
    const load = useCallback(() => { api.get("/notifications").then((r) => setData(r.data)).catch(() => {}); }, []);
    useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
    const openItem = async (n) => {
        setOpen(false);
        if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ } load(); }
        if (n.link) navigate(n.link);
    };
    const readAll = async () => { try { await api.post("/notifications/read-all"); } catch { /* ignore */ } load(); };
    return (
        <div className="relative">
            <button data-testid="notif-bell" onClick={() => { setOpen((v) => !v); if (!open) load(); }} className="relative p-1.5 rounded-sm hover:bg-white/10 transition-colors">
                <Bell className="w-4 h-4 text-white/80" />
                {data.unread > 0 && <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] px-1 flex items-center justify-center">{data.unread > 9 ? "9+" : data.unread}</span>}
            </button>
            {open && (<>
                <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                <div className="absolute right-0 top-[calc(100%+6px)] z-50 w-80 bg-white text-foreground rounded-md border border-border shadow-xl overflow-hidden" data-testid="notif-panel">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                        <span className="text-sm font-display font-bold">Notifications</span>
                        {data.unread > 0 && <button data-testid="notif-read-all" onClick={readAll} className="text-[11px] text-brand hover:underline">Mark all read</button>}
                    </div>
                    <div className="max-h-80 overflow-auto">
                        {data.items.length === 0 ? <div className="px-3 py-6 text-xs text-muted-foreground text-center">Nothing yet.</div>
                            : data.items.map((n) => (
                                <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)} className={`w-full text-left px-3 py-2.5 border-b border-border/60 hover:bg-slate-50 ${n.read ? "" : "bg-brand/[0.04]"}`}>
                                    <div className="flex items-center gap-2"><span className={`w-1.5 h-1.5 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-brand"}`} /><span className="text-[13px] font-medium">{n.title}</span></div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5 pl-3.5">{n.body}</div>
                                    <div className="text-[10px] text-muted-foreground/70 mt-0.5 pl-3.5">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                                </button>
                            ))}
                    </div>
                </div>
            </>)}
        </div>
    );
}

export default function AppLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [showChangePw, setShowChangePw] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const isAdmin = user?.role === "Admin";
    const isReviewer = user?.role === "External Reviewer";
    const reviewerScope = user?.reviewer_scope || {};
    const reviewerAdmin = isReviewer && (reviewerScope.admin !== false);
    const reviewerFirm = isReviewer && (reviewerScope.firm_records !== false);
    const FIRM_NAV = ["/service-charge-info", "/quotes"];
    const reviewerNavOk = (n) => {
        if (!isReviewer) return true;
        if (n.to === "/settings") return reviewerAdmin;
        if (FIRM_NAV.includes(n.to)) return reviewerFirm;
        return true;
    };
    // The restricted Admin role only sees Client Uploads + Quotes.
    const items = isAdmin
        ? nav.filter((n) => ADMIN_PATHS.includes(n.to))
        : nav.filter((n) => (!n.partnerOnly || user?.role === "Partner" || reviewerAdmin) && reviewerNavOk(n));
    if (isAdmin && !ADMIN_PATHS.includes(location.pathname)) {
        return <Navigate to="/service-charge-info" replace />;
    }
    const profileIncomplete = user && !["Client", "Admin", "External Reviewer"].includes(user.role) && (user.hourly_rate == null || user.ethics_ok == null || !user.years_experience);
    const mustChange = !!user?.must_change_password;

    return (
        <div className="h-screen flex flex-col bg-background">
            {isReviewer && (
                <div data-testid="reviewer-banner" className="shrink-0 bg-amber-500 text-black text-[12px] font-semibold flex items-center justify-center gap-2 px-4 py-1.5 tracking-wide">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                    External reviewer — read-only access. You can view but not change anything.
                </div>
            )}
            {/* Top ribbon — brand on the left, everything else under the user menu on the right */}
            <header className="h-11 shrink-0 bg-brand-ink text-white flex items-center px-4 border-b border-black/40 gap-6" data-testid="app-header">
                <button onClick={() => navigate("/")} className="flex items-center gap-2 shrink-0" data-testid="app-home-btn">
                    <img src="/qube-logo.svg" alt="Qube" className="w-6 h-6 rounded-sm" data-testid="app-logo" />
                    <div className="font-display font-bold text-[14px] tracking-tight">Qube <span className="text-white/60 font-medium">Hub</span></div>
                </button>

                <div className="ml-auto flex items-center gap-3 text-xs shrink-0 relative">
                    <NotificationBell />
                    <button
                        data-testid="header-user-menu-btn"
                        onClick={() => setMenuOpen((v) => !v)}
                        className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-white/10 transition-colors">
                        <div className="text-right leading-tight">
                            <div className="font-medium text-white text-[12px]" data-testid="header-user-name">{user?.name}</div>
                            <div className="text-white/50 uppercase tracking-wider text-[9px]">{user?.role}</div>
                        </div>
                        <div className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center font-display font-bold text-xs" data-testid="header-user-avatar">
                            {(user?.name || "?").trim().charAt(0).toUpperCase()}
                        </div>
                        <ChevronDown className={`w-4 h-4 text-white/70 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
                    </button>

                    {menuOpen && (
                        <>
                            <div className="fixed inset-0 z-40" data-testid="header-menu-backdrop" onClick={() => setMenuOpen(false)} />
                            <nav
                                data-testid="app-nav"
                                className="absolute right-0 top-[calc(100%+6px)] z-50 w-60 bg-white text-foreground rounded-md border border-border shadow-xl py-1.5 overflow-hidden">
                                {items.map((n) => (
                                    <NavLink
                                        key={n.to}
                                        to={n.to}
                                        end={n.to === "/"}
                                        data-testid={n.testId}
                                        onClick={() => setMenuOpen(false)}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                                isActive ? "bg-brand/[0.08] text-brand font-semibold" : "text-foreground/80 hover:bg-slate-50"
                                            }`
                                        }>
                                        <n.icon className="w-4 h-4" />
                                        {n.label}
                                    </NavLink>
                                ))}
                                <div className="my-1.5 border-t border-border" />
                                <button
                                    data-testid="header-change-pw-btn"
                                    onClick={() => { setMenuOpen(false); setShowChangePw(true); }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground/80 hover:bg-slate-50 transition-colors">
                                    <KeyRound className="w-4 h-4" /> Change password
                                </button>
                                <button
                                    data-testid="header-logout-btn"
                                    onClick={async () => { setMenuOpen(false); await logout(); navigate("/login"); }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors">
                                    <LogOut className="w-4 h-4" /> Sign out
                                </button>
                            </nav>
                        </>
                    )}
                </div>
            </header>

            {(showChangePw || mustChange) && <ChangePasswordDialog forced={mustChange} onClose={() => setShowChangePw(false)} />}

            {/* Main */}
            <main className="flex-1 overflow-auto" data-testid="app-main">
                {profileIncomplete && (
                    <div className="bg-amber-50 border-b border-amber-200 text-amber-900 px-4 py-2.5 text-sm flex items-center gap-2" data-testid="profile-incomplete-banner">
                        <SettingsIcon className="w-4 h-4 shrink-0" />
                        <span>Welcome! Please complete your profile — years of experience, hourly rate and ethics confirmation. This feeds A2 team &amp; time budgets.</span>
                        <button data-testid="profile-banner-complete-btn" onClick={() => navigate("/settings")} className="ml-auto shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium px-3 py-1 rounded-sm">Complete now</button>
                    </div>
                )}
                <Outlet />
            </main>
        </div>
    );
}
